/**
 * installChecklist.ts — INSTALL-CHECK-01 (Seth 2026-09-25: the job packet's
 * "Install Order" tab "need[s] to be a part of the install process … Incorporate
 * the checklist as part of the app closing process naturally and with what we
 * already have").
 *
 * The 24 lines of the paper Install Order, as an admin list (Settings → Install
 * Checklist). Each line names the equipment it applies to, so the installer only
 * sees the lines for what's going in — a furnace-only job never shows the AC
 * lines, the humidifier lines only show with a humidifier. Answers are Yes / No
 * / N/A like the paper (or text / a number). Lines 22–24 of the paper (who
 * soldered the line set, leak test psi, microns vacuum) are asked on Log the
 * Charge and shown here from there; the paper's comments, "notify office" and
 * signatures are the Wrap Up notes, Install Complete and the Sign-Off.
 *
 * Competitor frame: ServiceTitan triggers forms by job type / equipment / tags
 * and can require one before the tech closes out; Housecall Pro attaches
 * checklists by job type or line item; Jobber prompts (doesn't block) when a
 * required answer is blank. Pegasus: attached by the equipment on the job, a
 * "No" or a skipped line lands in the same "why?" the Install Complete button
 * already asks — never a lock-out.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { JobEquipment } from "./jobs";
import type { RefrigerantLog } from "./refrigerant";

export type CheckKind = "ynna" | "text" | "number";
export interface InstallCheckItem {
  id: string;
  key: string;
  label: string;
  kind: CheckKind;
  unit: string | null;
  applies_to: string[];
  required: boolean;
  sort: number;
  active: boolean;
}
export interface JobCheck {
  id: string;
  job_id: string;
  item_key: string;
  value: string;
  note: string | null;
  by_email: string | null;
  by_empno: string | null;
  at: string;
}

export const YNNA: { v: "yes" | "no" | "na"; label: string }[] = [
  { v: "yes", label: "Yes" }, { v: "no", label: "No" }, { v: "na", label: "N/A" },
];
export const ynnaLabel = (v: string | null | undefined) =>
  v === "yes" ? "Yes" : v === "no" ? "No" : v === "na" ? "N/A" : (v ?? "");

export function useInstallCheckItems(opts?: { all?: boolean }) {
  return useQuery({
    queryKey: ["install_check_items", !!opts?.all],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<InstallCheckItem[]> => {
      let q = supabase.from("pegasus_install_check_item").select("*").order("sort").order("label");
      if (!opts?.all) q = q.eq("active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as InstallCheckItem[];
    },
  });
}

export function useJobChecks(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["job_checks", jobId ?? null],
    enabled: !!jobId,
    queryFn: async (): Promise<Map<string, JobCheck>> => {
      const { data, error } = await supabase.from("pegasus_job_check").select("*").eq("job_id", jobId!);
      if (error) throw error;
      return new Map(((data ?? []) as JobCheck[]).map((c) => [c.item_key, c]));
    },
  });
}

export function useSetJobCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { jobId: string; key: string; value: string | null; note?: string | null; empno: string | null }) => {
      if (v.value == null || v.value === "") {
        const { error } = await supabase.from("pegasus_job_check").delete().eq("job_id", v.jobId).eq("item_key", v.key);
        if (error) throw error;
        return;
      }
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_job_check").upsert({
        job_id: v.jobId, item_key: v.key, value: v.value, note: (v.note ?? "").trim() || null,
        by_email: u?.user?.email ?? null, by_empno: v.empno, at: new Date().toISOString(),
      }, { onConflict: "job_id,item_key" });
      if (error) throw error;
    },
    onMutate: async (v) => {
      // Tap-tap-tap down the list: the answer shows at once.
      const key = ["job_checks", v.jobId];
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Map<string, JobCheck>>(key);
      const next = new Map(prev ?? []);
      if (v.value == null || v.value === "") next.delete(v.key);
      else next.set(v.key, { id: prev?.get(v.key)?.id ?? `tmp-${v.key}`, job_id: v.jobId, item_key: v.key, value: v.value, note: v.note ?? null, by_email: null, by_empno: v.empno, at: new Date().toISOString() });
      qc.setQueryData(key, next);
      return { prev, key };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev); },
    onSettled: (_d, _e, v) => { void qc.invalidateQueries({ queryKey: ["job_checks", v.jobId] }); },
  });
}

/** The lines that apply to this job: a line with no equipment listed applies to every install. */
export function checklistFor(items: InstallCheckItem[], units: JobEquipment[]): InstallCheckItem[] {
  const keys = new Set(units.map((u) => u.equip_key).filter(Boolean) as string[]);
  return items.filter((it) => it.active && (it.applies_to.length === 0 || it.applies_to.some((k) => keys.has(k))));
}

/** Lines answered by another step (Log the Charge) — shown filled, the crew can still change them. */
export function autoAnswers(logs: RefrigerantLog[]): Map<string, { value: string; from: string }> {
  const m = new Map<string, { value: string; from: string }>();
  const psi = logs.find((l) => l.kind === "charge" && l.leak_test_psi != null && l.leak_test_psi > 0);
  if (psi) m.set("ac_leak_check", { value: "yes", from: `Leak tested at ${psi.leak_test_psi} psi on Log the Charge` });
  return m;
}

/** How far along the checklist is; `missing` = required lines not answered. `no` = lines answered No. */
export function checklistProgress(lines: InstallCheckItem[], answers: Map<string, JobCheck> | undefined, auto: Map<string, { value: string }>) {
  const valueOf = (k: string) => answers?.get(k)?.value ?? auto.get(k)?.value ?? null;
  const answered = lines.filter((l) => !!valueOf(l.key));
  const missing = lines.filter((l) => l.required && !valueOf(l.key));
  const no = lines.filter((l) => l.kind === "ynna" && valueOf(l.key) === "no");
  return { total: lines.length, answered: answered.length, missing, no, valueOf };
}

/* ------------------------------------------------------ Settings (admin) */

export function useSaveCheckItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<InstallCheckItem> & { label: string }) => {
      const patch = {
        label: v.label.trim(), kind: v.kind ?? "ynna", unit: v.unit ?? null,
        applies_to: v.applies_to ?? [], required: v.required ?? true,
        active: v.active ?? true, updated_at: new Date().toISOString(),
      };
      if (v.id) {
        const { error } = await supabase.from("pegasus_install_check_item").update({ ...patch, ...(v.sort != null ? { sort: v.sort } : {}) }).eq("id", v.id);
        if (error) throw error;
      } else {
        const { data: u } = await supabase.auth.getUser();
        const key = v.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) + "_" + Math.random().toString(36).slice(2, 6);
        const { error } = await supabase.from("pegasus_install_check_item").insert({ ...patch, key, sort: v.sort ?? 1000, created_by: u?.user?.email ?? null });
        if (error) throw error;
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["install_check_items"] }); },
  });
}

export function useMoveCheckItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { list: InstallCheckItem[]; id: string; dir: -1 | 1 }) => {
      const i = v.list.findIndex((x) => x.id === v.id);
      const j = i + v.dir;
      if (i < 0 || j < 0 || j >= v.list.length) return;
      const reordered = [...v.list];
      [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
      await Promise.all(reordered.map((it, n) =>
        it.sort === (n + 1) * 10 ? null : supabase.from("pegasus_install_check_item").update({ sort: (n + 1) * 10 }).eq("id", it.id)));
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["install_check_items"] }); },
  });
}
